import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import filesAPI from '../api/files';
import foundersAPI, {
  Founder,
  FounderDetails,
  FounderId,
  FounderStatus,
} from '../api/founders';
import venturesAPI from '../api/ventures';
import { Venture } from '../api/ventures/types/Venture';
import { SnackMessage } from '../components/common';
import { massAsyncRequest } from '../utils/api';
import { isMobile } from '../utils/functions';

interface FoundersProviderProps {
  children: ReactNode;
}

export interface FoundersContextProps {
  resetKey: number;
  founders: Founder[];
  foundersLogos: { [x: string]: string | null };
  selectedFounderIds: Founder['id'][];
  assignTargetFounder: Founder | null;
  archiveTargetFounders: Founder[] | null;
  isAssigningFounder: { [x: string]: boolean };
  isSendingProfileUpdate: boolean;
  isChangingFounderStatus: boolean;
  inviteTargetFounderId: FounderId | null;
  profileUpdateTargetFounderId: FounderId | null;
  loadedVenturesById: {
    [x: string]: Venture[];
  };
  setFounders: Dispatch<SetStateAction<Founder[]>>;
  setSelectedFounderIds: Dispatch<SetStateAction<Founder['id'][]>>;
  setIsSendingProfileUpdate: Dispatch<SetStateAction<boolean>>;
  setInviteTargetFounderId: Dispatch<SetStateAction<FounderId | null>>;
  handleAssingSuccessForm: (
    founderId: Founder['id'],
    handleClose: () => void,
  ) => (data: {
    assigned: string[];
    removed: string[];
    all: Venture[];
  }) => Promise<void>;
  handleVenturesLoaded: (
    founderId: Founder['id'],
  ) => (loadedVentures: Venture[]) => void;
  setProfileUpdateTargetFounderId: Dispatch<SetStateAction<FounderId | null>>;
  setAssignTargetFounder: Dispatch<SetStateAction<Founder | null>>;
  setArchiveTargetFounders: Dispatch<SetStateAction<Founder[] | null>>;
  sendFounderUpdateProfilesTarget: (onClose: () => {}) => Promise<void>;
  sendFounderUpdateProfilesSelected: (onClose: () => {}) => Promise<void>;
  changeFounderStatus: (status: FounderStatus) => Promise<void>;
}

export const FoundersContext = createContext<FoundersContextProps>({
  resetKey: 0,
  founders: [],
  foundersLogos: {},
  selectedFounderIds: [],
  assignTargetFounder: null,
  archiveTargetFounders: null,
  isAssigningFounder: {},
  isSendingProfileUpdate: false,
  isChangingFounderStatus: false,
  inviteTargetFounderId: null,
  profileUpdateTargetFounderId: null,
  loadedVenturesById: {},
  setFounders: () => {},
  setSelectedFounderIds: () => {},
  setIsSendingProfileUpdate: () => {},
  setInviteTargetFounderId: () => {},
  handleAssingSuccessForm:
    () =>
    // eslint-disable-next-line no-empty-pattern
    async ({ assigned: [], removed: [], all: [] }) => {},
  handleVenturesLoaded: () => () => {},
  setProfileUpdateTargetFounderId: () => {},
  setAssignTargetFounder: () => {},
  setArchiveTargetFounders: () => {},
  sendFounderUpdateProfilesTarget: async () => {},
  sendFounderUpdateProfilesSelected: async () => {},
  changeFounderStatus: async () => {},
});

const validateFounder = (founder: Founder): FounderDetails => {
  return {
    founderNotes: founder.founderNotes,
    address: founder.address,
    status: founder.status,
    firstName: founder.firstName,
    lastName: founder.lastName,
    about: founder.about,
    currentRole: founder.currentRole,
    education: founder.education,
    isSerialFounder: founder.isSerialFounder,
    dateOfBirth: founder.dateOfBirth,
    dateOfEnrollment: founder.dateOfEnrollment,
    linkedInProfile: founder.linkedInProfile,
    email: founder.email,
    phone: founder.phone,
    logo: founder.logo,
    country: founder.country,
    state: founder.state,
    city: founder.city,
    zip: founder.zip,
  };
};

export const FoundersProvider = ({ children }: FoundersProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const [founders, setFounders] = useState<Founder[]>([]);
  const [foundersLogos, setFoundersLogos] = useState<{
    [x: string]: string | null;
  }>({});
  const [assignTargetFounder, setAssignTargetFounder] =
    useState<Founder | null>(null);
  const [isAssigningFounder, setIsAssigningFounder] = useState<{
    [x: string]: boolean;
  }>({});
  const [profileUpdateTargetFounderId, setProfileUpdateTargetFounderId] =
    useState<FounderId | null>(null);
  const [inviteTargetFounderId, setInviteTargetFounderId] =
    useState<FounderId | null>(null);
  const [isSendingProfileUpdate, setIsSendingProfileUpdate] = useState(false);
  const [loadedVenturesById, setLoadedVenturesById] = useState<{
    [x: string]: Venture[];
  }>({});
  const [selectedFounderIds, setSelectedFounderIds] = useState<Founder['id'][]>(
    [],
  );
  const [archiveTargetFounders, setArchiveTargetFounders] = useState<
    Founder[] | null
  >(null);
  const [isChangingFounderStatus, setIsChangingFounderStatus] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const reset = useCallback(() => setResetKey((prev) => prev + 1), []);

  const handleVenturesLoaded = useCallback(
    (founderId: Founder['id']) => (loadedVentures: Venture[]) => {
      setFounders((prevFounders) =>
        prevFounders.map((founder) =>
          founder.id === founderId
            ? { ...founder, assigned: loadedVentures.length }
            : founder,
        ),
      );
      setLoadedVenturesById((prevState) => ({
        ...prevState,
        [founderId]: loadedVentures,
      }));
    },
    [],
  );

  const handleAssingSuccessForm = useCallback(
    (founderId: Founder['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Venture[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsAssigningFounder((prev) => ({ ...prev, [founderId]: true }));
          const assignedRequests = assigned.map(
            (ventureId) => () =>
              venturesAPI.assignFounder(ventureId, founderId),
          );
          const removedRequests = removed.map(
            (ventureId) => () =>
              venturesAPI.removeFounderAssignment(ventureId, founderId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleVenturesLoaded(founderId)(all);
          handleModalClose();
          setIsAssigningFounder((prev) => ({ ...prev, [founderId]: false }));
        } catch (e: any) {
          console.log('error', 'e');
        }
      },
    [handleVenturesLoaded],
  );

  const handleSendFounderUpdateProfiles = useCallback(
    async (founderIds: FounderId[], onClose: () => {}) => {
      try {
        setIsSendingProfileUpdate(true);
        await foundersAPI.sendFounderUpdateInvites(founderIds);
        enqueueSnackbar('The profile update requests were successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [enqueueSnackbar],
  );

  const sendFounderUpdateProfilesTarget = useCallback(
    async (onClose: () => {}) => {
      try {
        if (!profileUpdateTargetFounderId) {
          return;
        }

        await handleSendFounderUpdateProfiles(
          [profileUpdateTargetFounderId],
          onClose,
        );

        setProfileUpdateTargetFounderId(null);
      } catch (e) {
        console.error('Error sending founder update profiles:', e);
      }
    },
    [handleSendFounderUpdateProfiles, profileUpdateTargetFounderId],
  );

  const sendFounderUpdateProfilesSelected = useCallback(
    async (onClose: () => {}) => {
      try {
        if (!selectedFounderIds.length) {
          return;
        }

        await handleSendFounderUpdateProfiles(selectedFounderIds, onClose);

        setSelectedFounderIds([]);
      } catch (e) {
        console.error('Error sending founder update profiles:', e);
      }
    },
    [handleSendFounderUpdateProfiles, selectedFounderIds],
  );

  const loadFoundersLogos = async (foundersList: Founder[]) => {
    try {
      const founderImages = foundersList.map((founder: Founder) => ({
        id: founder.id,
        logo: founder.logo,
      }));
      setFoundersLogos(() => {
        const sources = founderImages.reduce((res, founder) => {
          res[founder.id] = founder.logo ? '' : null;
          return res;
        }, {} as { [x: string]: string | null });
        return sources;
      });
      const shouldBeLoaded = founderImages.filter(
        (founderImage) => !!founderImage.logo,
      );
      const logos = shouldBeLoaded.map((founderImage) => founderImage.logo);
      if (!logos.length) {
        return false;
      }
      if (!logos.every((logo): logo is string => !!logo)) {
        return false;
      }

      const loadedLogos = await filesAPI.getFileLogos(logos);

      const nextState = shouldBeLoaded.reduce(
        (res, founderImage, founderImageIndex) => {
          res[founderImage.id] = loadedLogos[founderImageIndex];
          return res;
        },
        {} as { [x: string]: string },
      );
      return setFoundersLogos((prevFounders) => ({
        ...prevFounders,
        ...nextState,
      }));
    } catch (e: any) {
      console.log('loadFoundersLogos error');
      return false;
    }
  };

  const changeFounderStatus = useCallback(
    async (status: FounderStatus) => {
      try {
        if (!archiveTargetFounders?.length) return;
        setIsChangingFounderStatus(true);
        const changeStatus = archiveTargetFounders.map((value: Founder) => {
          return () =>
            foundersAPI.update(value.id, {
              ...validateFounder(value),
              status,
            });
        });
        const res = await massAsyncRequest(changeStatus);

        if (res.some((value) => value === undefined)) {
          throw new Error('An error occurred while changing founder status.');
        }
      } catch (e: any) {
        console.error(e);
        enqueueSnackbar(
          'An error occurred while changing founder status. Please, try again.',
          { variant: 'error' },
        );
      } finally {
        // reset to force reload
        if (isMobile()) {
          if (!searchParams?.search) {
            setFounders((prev) =>
              prev.filter((founder) =>
                archiveTargetFounders?.some((tf) => tf.id !== founder.id),
              ),
            );
          } else {
            setFounders((prev) =>
              prev.map((founder) =>
                archiveTargetFounders?.some((tf) => tf.id === founder.id)
                  ? { ...founder, status }
                  : founder,
              ),
            );
          }
        } else {
          reset();
        }

        setArchiveTargetFounders(null);
        setIsChangingFounderStatus(false);
      }
    },
    [archiveTargetFounders, searchParams, enqueueSnackbar, reset],
  );

  useEffect(() => {
    if (founders.length) {
      loadFoundersLogos(founders);
    }
  }, [founders]);

  const contextValue: FoundersContextProps = {
    resetKey,
    founders,
    foundersLogos,
    selectedFounderIds,
    assignTargetFounder,
    archiveTargetFounders,
    isAssigningFounder,
    isSendingProfileUpdate,
    isChangingFounderStatus,
    inviteTargetFounderId,
    profileUpdateTargetFounderId,
    loadedVenturesById,
    setFounders,
    setSelectedFounderIds,
    setIsSendingProfileUpdate,
    setInviteTargetFounderId,
    handleAssingSuccessForm,
    handleVenturesLoaded,
    setProfileUpdateTargetFounderId,
    setAssignTargetFounder,
    sendFounderUpdateProfilesTarget,
    sendFounderUpdateProfilesSelected,
    setArchiveTargetFounders,
    changeFounderStatus,
  };

  return (
    <FoundersContext.Provider value={contextValue}>
      {children}
    </FoundersContext.Provider>
  );
};

export const useFoundersContext = () => {
  const context = useContext(FoundersContext);
  if (!context) {
    throw new Error(
      'useFoundersContext must be used within a FoundersProvider',
    );
  }
  return context;
};
